import React, { useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { MenuItem, CircularProgress } from '@mui/material';
import { mkConfig, generateCsv, download } from "export-to-csv";

import { useActiveAnalysis, useIdentifiedSpecies } from '../services/api/KeynomeAPIHooks.js';
import { sortByIdConfidence } from './utilities.js';


export default function ExportIdentifiedSpeciesMenuItem(props) {   
    const {
        isIllumina,
        isDatasetLevel,
        run,
        samples,
        handleMenuClose,
        oneMenuItemClicked,
        setOneMenuItemClicked,
    } = props;
    const { setUserErrorMessage, setProgressMessage, hasAccessReportViruses } = useOutletContext();

    const { data: activeAnalyses, isLoading: analysesLoading } = useActiveAnalysis(samples?.map(sample => sample?.id));
    const { data: identifiedSpecies, isLoading: identifiedSpeciesLoading } = useIdentifiedSpecies(
        activeAnalyses
            ?.filter(analysis => analysis?.attributes.status === "succeeded")
            ?.map(analysis => analysis?.id)
    );
    const dataLoading = analysesLoading || identifiedSpeciesLoading;

    const organismIdRows = useMemo(() => {
        if (dataLoading) return [];
    
        return samples?.map(sample => {
            const _activeAnalysis = activeAnalyses?.find(analysis => analysis?.attributes?.sample_id === sample?.id);
            const _identifiedSpecies = sortByIdConfidence(
                identifiedSpecies
                    ?.filter(species => species.attributes.analyses_id === _activeAnalysis?.id)
                    ?.filter(species => (hasAccessReportViruses || !species.attributes.is_virus))
            );

            if (!!!_activeAnalysis || _identifiedSpecies.length === 0) return [];

            const totalMicrobialMegabases = _identifiedSpecies.reduce((sum, species) => sum + (species.attributes.reads_megabases_to_species ?? 0), 0);
            return _identifiedSpecies?.map(species => {
                const identifiedSpeciesRow = {
                    "dataset_name": run.attributes.name,
                    "sample_name": sample.attributes.sample_name,
                    "sample_type": sample.attributes.sample_type,
                    "analysis_pipeline_version": _activeAnalysis?.attributes.metadata.minion_pipeline_version,
                    "analysis_started_at": _activeAnalysis.attributes.created_at,
                    "analysis_completed_at": _activeAnalysis.attributes.updated_at,
                    "organism": species.attributes.species,
                    [isIllumina ? "read_pairs_to_organism" : "reads_to_organism"]: species.attributes.reads_to_species || "",
                    "megabases_to_organism": species.attributes.reads_megabases_to_species || "",
                    "percent_microbial_megabases_to_organism": (
                        (species.attributes.reads_megabases_to_species && totalMicrobialMegabases) ?
                        species.attributes.reads_megabases_to_species / totalMicrobialMegabases * 100 : ""
                    ),
                };
        
                if (isIllumina) {
                    return {
                        ...identifiedSpeciesRow,
                        "contigs_to_species": species.attributes.contigs_to_species,
                        "assembly_megabases_to_species": species.attributes.assembly_megabases_to_species,
                    };
                }
                
                return {
                    ...identifiedSpeciesRow,
                    "kid_call_positive": species.attributes.species_call_positive,
                    "kid_value": species.attributes.species_call_raw_value,
                    "percent_reference_coverage": species.attributes.perc_nonzero_coverage * 100 || "",
                    "average_depth": species.attributes.avg_depth || "",
                };
            })
        }).flat()
    }, [run, samples, dataLoading, activeAnalyses, identifiedSpecies, hasAccessReportViruses, isIllumina]);

    const handleExportClick = useCallback(async () => {
        setOneMenuItemClicked(true);
        setProgressMessage({"status": "inProgress", "message": "Exporting Identified Organisms report"});

        try {
            const csvConfig = mkConfig({
                useKeysAsHeaders: true,
                filename: (isDatasetLevel ? run.attributes.name : samples[0].attributes.sample_name ) + '_organism_id'
            });
            const csv = generateCsv(csvConfig)(organismIdRows);
            download(csvConfig)(csv);

            setProgressMessage({"status": "completed", "message": "Identified Organisms export completed."});
        }
        catch (error) {
            setProgressMessage(null);
            setUserErrorMessage(
                'Failed to export Identified Organisms! Please wait a few minutes and try again. Contact support@dayzerodiagnostics.com if you require assistance.'
            );
            throw new Error(`Request to export Identified Organisms for ${samples?.map(sample => sample.attributes.sample_name)} in dataset ${run.attributes.name} failed: ${error}`);
        }
        finally {
            setOneMenuItemClicked(false);
        }
    }, [setOneMenuItemClicked, isDatasetLevel, run, samples, organismIdRows, setProgressMessage, setUserErrorMessage]);

    return <MenuItem disabled={oneMenuItemClicked || dataLoading} onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        handleExportClick();
        handleMenuClose();
    }}>
        Export Identified Organisms
        {dataLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
    </MenuItem>
}